import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { FirebaseContext } from '../../Firebase'

/**
 * Hardcoded sample goal content used to seed and render sample-goal UI.
 *
 * Each step key must remain stable because it is persisted in Firestore.
 *
 * @type {Record<string, {
 * id: string,
 * titleId: string,
 * defaultTitle: string,
 * description: string,
 * origin: string,
 * steps: Array<{ key: string, labelId: string, defaultLabel: string, description: string }>
 * }>}
 */
export const SAMPLE_GOAL_DEFINITIONS = {
	starterCareerExploration: {
		id: 'starterCareerExploration',
		titleId: 'goal-tracking-sample-goal-title',
		defaultTitle: 'Set three goals in Wingspans to help you achieve your dreams',
		origin: 'sample-goal-seed',
		steps: [
			{
				key: 'setSmartGoals',
				labelId: 'goal-tracking-sample-goal-step-smart',
				defaultLabel: 'Make them SMART goals - specific, measurable, achievable, relevant, and time-bound.',
			},
			{
				key: 'addTargetDate',
				labelId: 'goal-tracking-sample-goal-step-target-date',
				defaultLabel: 'Give each one a target date.',
			},
			{
				key: 'breakIntoSteps',
				labelId: 'goal-tracking-sample-goal-step-break-steps',
				defaultLabel: 'Break them into steps, and check each one off as you complete it.',
			},
			{
				key: 'linkActionPlan',
				labelId: 'goal-tracking-sample-goal-step-link-action-plan',
				defaultLabel: 'Link them to your Action Plan so they stay front and center.',
			},
			{
				key: 'reachHigh',
				labelId: 'goal-tracking-sample-goal-step-reach-high',
				defaultLabel: 'Reach high!',
			},
		],
	},
}

/**
 * Default server-side user record shape for sample goal progress.
 *
 * @type {{ complete: boolean, completedStepKeys: string[] }}
 */
const DEFAULT_USER_SAMPLE_GOAL = {
	complete: false,
	completedStepKeys: [],
}

/**
 * Ensures returned callable data always has the expected shape.
 *
 * @param {Object|undefined|null} data
 * @returns {{ complete: boolean, completedStepKeys: string[] }}
 */
const normalizeUserSampleGoalData = (data) => {
	const completedStepKeys = Array.isArray(data?.completedStepKeys)
		? data.completedStepKeys.filter((stepKey) => typeof stepKey === 'string' && stepKey.trim())
		: []

	return {
		complete: Boolean(data?.complete),
		completedStepKeys,
	}
}

/**
 * Returns full sample-goal metadata enriched with completion state.
 *
 * @param {string} goalId
 * @param {{ complete: boolean, completedStepKeys: string[] }} userData
 * @returns {{
 * id: string,
 * title: string,
 * description: string,
 * origin: string,
 * complete: boolean,
 * completedStepCount: number,
 * totalStepCount: number,
 * progressRatio: number,
 * steps: Array<{ key: string, label: string, description: string, complete: boolean }>
 * }|null}
 */
const buildSampleGoalState = (goalId, userData, formatMessage) => {
	const definition = SAMPLE_GOAL_DEFINITIONS[goalId]
	if (!definition) return null

	const title = definition.titleId
		? formatMessage({ id: definition.titleId, defaultMessage: definition.defaultTitle })
		: definition.title

	const completedStepKeySet = new Set(userData?.completedStepKeys ?? [])
	const steps = definition.steps.map((step) => ({
		...step,
		label: step.labelId
			? formatMessage({ id: step.labelId, defaultMessage: step.defaultLabel })
			: step.label,
		complete: completedStepKeySet.has(step.key),
	}))

	const completedStepCount = steps.filter((step) => step.complete).length
	const totalStepCount = steps.length
	const progressRatio = totalStepCount > 0 ? completedStepCount / totalStepCount : 0

	return {
		id: definition.id,
		title,
		description: definition.description,
		origin: definition.origin,
		complete: Boolean(userData?.complete),
		completedStepCount,
		totalStepCount,
		progressRatio,
		steps,
	}
}

/**
 * Hook for loading and updating a hardcoded sample goal with user-specific
 * completion data stored via callable Firebase functions.
 *
 * @param {Object} options
 * @param {string} [options.goalId='starterCareerExploration']
 * @param {boolean} [options.autoLoad=true]
 * @returns {{
 * goal: ReturnType<typeof buildSampleGoalState>,
 * userData: { complete: boolean, completedStepKeys: string[] },
 * loading: boolean,
 * saving: boolean,
 * error: Error|null,
 * refresh: () => Promise<{ complete: boolean, completedStepKeys: string[] }>,
 * setComplete: (complete: boolean) => Promise<boolean>,
 * addCompletedStep: (stepKey: string) => Promise<boolean>,
 * removeCompletedStep: (stepKey: string) => Promise<boolean>,
 * toggleStepComplete: (stepKey: string, nextComplete?: boolean) => Promise<boolean>
 * }}
 */
const useSampleGoal = ({
	goalId = 'starterCareerExploration',
	autoLoad = true,
} = {}) => {
	const intl = useIntl()
	const { firebase } = useContext(FirebaseContext)

	const [userData, setUserData] = useState(DEFAULT_USER_SAMPLE_GOAL)
	const [loading, setLoading] = useState(Boolean(autoLoad))
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)

	const hasGoalDefinition = Boolean(SAMPLE_GOAL_DEFINITIONS[goalId])

	const getUserSampleGoal = useCallback(async () => {
		if (!firebase?.getUserSampleGoal) {
			throw new Error('Firebase sample goal methods are not available in context.')
		}

		const result = await firebase.getUserSampleGoal()
		return normalizeUserSampleGoalData(result)
	}, [firebase])

	const saveUserSampleGoal = useCallback(async ({ complete, stepKey, stepAction } = {}) => {
		if (!firebase?.saveUserSampleGoal) {
			throw new Error('Firebase sample goal methods are not available in context.')
		}

		const payload = {}

		if (typeof complete === 'boolean') {
			payload.complete = complete
		}

		if (typeof stepKey === 'string' && stepKey.trim()) {
			payload.stepKey = stepKey
		}

		if (typeof stepAction === 'string' && stepAction.trim()) {
			payload.stepAction = stepAction
		}

		const result = await firebase.saveUserSampleGoal(payload)
		return result
	}, [firebase])

	const refresh = useCallback(async () => {
		setLoading(true)
		setError(null)

		try {
			const nextData = await getUserSampleGoal()
			setUserData(nextData)
			return nextData
		} catch (nextError) {
			setError(nextError)
			return null
		} finally {
			setLoading(false)
		}
	}, [getUserSampleGoal])

	const setComplete = useCallback(async (complete) => {
		const nextComplete = Boolean(complete)
		const previousUserData = userData

		setSaving(true)
		setError(null)
		setUserData((prev) => ({
			...prev,
			complete: nextComplete,
		}))

		try {
			await saveUserSampleGoal({ complete: nextComplete })
			return true
		} catch (nextError) {
			setError(nextError)
			setUserData(previousUserData)
			return false
		} finally {
			setSaving(false)
		}
	}, [saveUserSampleGoal, userData])

	const addCompletedStep = useCallback(async (stepKey) => {
		if (!stepKey || typeof stepKey !== 'string') {
			return false
		}

		const previousUserData = userData

		setSaving(true)
		setError(null)
		setUserData((prev) => {
			const nextSet = new Set(prev.completedStepKeys)
			nextSet.add(stepKey)

			return {
				...prev,
				completedStepKeys: Array.from(nextSet),
			}
		})

		try {
			await saveUserSampleGoal({ stepKey, stepAction: 'add' })
			return true
		} catch (nextError) {
			setError(nextError)
			setUserData(previousUserData)
			return false
		} finally {
			setSaving(false)
		}
	}, [saveUserSampleGoal, userData])

	const removeCompletedStep = useCallback(async (stepKey) => {
		if (!stepKey || typeof stepKey !== 'string') {
			return false
		}

		const previousUserData = userData

		setSaving(true)
		setError(null)
		setUserData((prev) => ({
			...prev,
			completedStepKeys: prev.completedStepKeys.filter((key) => key !== stepKey),
		}))

		try {
			await saveUserSampleGoal({ stepKey, stepAction: 'remove' })
			return true
		} catch (nextError) {
			setError(nextError)
			setUserData(previousUserData)
			return false
		} finally {
			setSaving(false)
		}
	}, [saveUserSampleGoal, userData])

	const toggleStepComplete = useCallback(async (stepKey, nextComplete) => {
		const currentlyComplete = userData.completedStepKeys.includes(stepKey)
		const shouldComplete = typeof nextComplete === 'boolean' ? nextComplete : !currentlyComplete

		return shouldComplete ? addCompletedStep(stepKey) : removeCompletedStep(stepKey)
	}, [addCompletedStep, removeCompletedStep, userData.completedStepKeys])

	useEffect(() => {
		if (!autoLoad || !hasGoalDefinition) {
			setLoading(false)
			return
		}

		refresh().catch(() => null)
	}, [autoLoad, hasGoalDefinition, refresh])

	const goal = useMemo(() => {
		if (!hasGoalDefinition) return null
		const safeFormatMessage =
			typeof intl?.formatMessage === 'function'
				? intl.formatMessage
				: ({ defaultMessage, id }) => defaultMessage || id || ''

		return buildSampleGoalState(goalId, userData, safeFormatMessage)
	}, [goalId, hasGoalDefinition, intl, userData])

	return {
		goal,
		userData,
		loading,
		saving,
		error,
		refresh,
		setComplete,
		addCompletedStep,
		removeCompletedStep,
		toggleStepComplete,
	}
}

export default useSampleGoal
